import React, { useState, useEffect } from 'react';
import { Building, Save, RotateCcw, AlertCircle, CheckCircle } from 'lucide-react';
import { workdayService } from '../../services/workdayService';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday', short: 'Mon' },
  { value: 1, label: 'Tuesday', short: 'Tue' },
  { value: 2, label: 'Wednesday', short: 'Wed' },
  { value: 3, label: 'Thursday', short: 'Thu' },
  { value: 4, label: 'Friday', short: 'Fri' },
  { value: 5, label: 'Saturday', short: 'Sat' },
  { value: 6, label: 'Sunday', short: 'Sun' }
];

const FirmWorkdayDefaultsManager = () => {
  const [defaults, setDefaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadFirmDefaults();
  }, []);

  const loadFirmDefaults = async () => {
    try {
      setLoading(true);
      const response = await workdayService.getFirmWorkdayDefaults();
      
      // Create a map by day_of_week for easy lookup
      const defaultsMap = {};
      response.data.forEach(def => {
        defaultsMap[def.day_of_week] = def;
      });
      
      // Create array with all 7 days, filling in missing days with defaults
      const fullDefaults = DAYS_OF_WEEK.map(day => {
        const existing = defaultsMap[day.value];
        if (existing) {
          return {
            ...existing,
            start_time: existing.start_time || '09:00',
            end_time: existing.end_time || '17:00',
            lunch_start_time: existing.lunch_start_time || '12:00',
            lunch_end_time: existing.lunch_end_time || '13:00'
          };
        }
        
        // Default for missing days
        return {
          day_of_week: day.value,
          is_working_day: day.value < 5, // Monday-Friday default
          start_time: day.value < 5 ? '09:00' : null,
          end_time: day.value < 5 ? '17:00' : null,
          lunch_start_time: day.value < 5 ? '12:00' : null,
          lunch_end_time: day.value < 5 ? '13:00' : null
        };
      });
      
      setDefaults(fullDefaults);
    } catch (error) {
      console.error('Failed to load firm workday defaults:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load firm workday defaults. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDayChange = (dayOfWeek, field, value) => {
    setDefaults(prev => prev.map(def => 
      def.day_of_week === dayOfWeek 
        ? { ...def, [field]: value }
        : def
    ));
    
    // Clear any existing messages
    setMessage({ type: '', text: '' });
  };

  const handleSave = async (dayOfWeek) => {
    try {
      setSaving(true);
      const dayDefault = defaults.find(def => def.day_of_week === dayOfWeek);
      
      const updateData = {
        is_working_day: dayDefault.is_working_day,
        start_time: dayDefault.start_time,
        end_time: dayDefault.end_time,
        lunch_start_time: dayDefault.lunch_start_time,
        lunch_end_time: dayDefault.lunch_end_time
      };
      
      await workdayService.updateFirmWorkdayDefault(dayOfWeek, updateData);
      
      setMessage({
        type: 'success',
        text: `${DAYS_OF_WEEK[dayOfWeek].label} defaults saved successfully!`
      });
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      console.error('Failed to save firm workday default:', error);
      setMessage({
        type: 'error',
        text: 'Failed to save firm workday defaults. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      
      // Save all days sequentially
      for (const def of defaults) {
        const updateData = {
          is_working_day: def.is_working_day,
          start_time: def.start_time,
          end_time: def.end_time,
          lunch_start_time: def.lunch_start_time,
          lunch_end_time: def.lunch_end_time
        };
        
        await workdayService.updateFirmWorkdayDefault(def.day_of_week, updateData);
      }
      
      setMessage({
        type: 'success',
        text: 'All firm workday defaults saved successfully!'
      });
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      console.error('Failed to save firm workday defaults:', error);
      setMessage({
        type: 'error',
        text: 'Failed to save firm workday defaults. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    loadFirmDefaults();
    setMessage({ type: '', text: '' });
  };

  const applyTemplate = (template) => {
    let newDefaults = [...defaults];
    
    switch (template) {
      case 'standard':
        // Standard 9-5 weekdays
        newDefaults = DAYS_OF_WEEK.map(day => ({
          day_of_week: day.value,
          is_working_day: day.value < 5,
          start_time: day.value < 5 ? '09:00' : null,
          end_time: day.value < 5 ? '17:00' : null,
          lunch_start_time: day.value < 5 ? '12:00' : null,
          lunch_end_time: day.value < 5 ? '13:00' : null
        }));
        break;
        
      case 'extended':
        // Extended hours 8-6 weekdays
        newDefaults = DAYS_OF_WEEK.map(day => ({
          day_of_week: day.value,
          is_working_day: day.value < 5,
          start_time: day.value < 5 ? '08:00' : null,
          end_time: day.value < 5 ? '18:00' : null,
          lunch_start_time: day.value < 5 ? '12:00' : null,
          lunch_end_time: day.value < 5 ? '13:00' : null
        }));
        break;
        
      case 'saturday':
        // Include Saturday mornings
        newDefaults = DAYS_OF_WEEK.map(day => ({
          day_of_week: day.value,
          is_working_day: day.value < 6, // Monday-Saturday
          start_time: day.value < 5 ? '09:00' : day.value === 5 ? '09:00' : null,
          end_time: day.value < 5 ? '17:00' : day.value === 5 ? '13:00' : null,
          lunch_start_time: day.value < 5 ? '12:00' : null,
          lunch_end_time: day.value < 5 ? '13:00' : null
        }));
        break;
        
      default:
        return;
    }
    
    setDefaults(newDefaults);
    setMessage({ type: '', text: '' });
  };

  const renderDayDefault = (dayDefault, dayInfo) => {
    return (
      <div key={dayDefault.day_of_week} className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">{dayInfo.label}</h4>
          <div className="flex items-center space-x-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={dayDefault.is_working_day}
                onChange={(e) => handleDayChange(dayDefault.day_of_week, 'is_working_day', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
              <span className="ml-3 text-sm text-gray-700">
                {dayDefault.is_working_day ? 'Working Day' : 'Day Off'}
              </span>
            </label>
            
            <button
              onClick={() => handleSave(dayDefault.day_of_week)}
              disabled={saving}
              className="px-2 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </div>
        
        {dayDefault.is_working_day && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Time
              </label>
              <input
                type="time"
                value={dayDefault.start_time || ''}
                onChange={(e) => handleDayChange(dayDefault.day_of_week, 'start_time', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Time
              </label>
              <input
                type="time"
                value={dayDefault.end_time || ''}
                onChange={(e) => handleDayChange(dayDefault.day_of_week, 'end_time', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lunch Start
              </label>
              <input
                type="time"
                value={dayDefault.lunch_start_time || ''}
                onChange={(e) => handleDayChange(dayDefault.day_of_week, 'lunch_start_time', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lunch End
              </label>
              <input
                type="time"
                value={dayDefault.lunch_end_time || ''}
                onChange={(e) => handleDayChange(dayDefault.day_of_week, 'lunch_end_time', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Building className="h-5 w-5 text-gray-500 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Firm Default Working Hours</h3>
          </div>
          
          <div className="flex items-center space-x-3">
            <select
              onChange={(e) => applyTemplate(e.target.value)}
              value=""
              className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
            >
              <option value="">Apply Template...</option>
              <option value="standard">Standard (9-5 weekdays)</option>
              <option value="extended">Extended (8-6 weekdays)</option>
              <option value="saturday">Include Saturday mornings</option>
            </select>
            
            <button
              onClick={handleReset}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900 flex items-center"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset
            </button>
          </div>
        </div>
        
        <p className="mt-2 text-sm text-gray-600">
          Set default working hours for your firm. These defaults apply to all users unless they have individual overrides.
        </p>
      </div>

      <div className="p-6">
        {message.text && (
          <div className={`mb-6 p-4 rounded-md flex items-center ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="h-5 w-5 mr-2" />
            ) : (
              <AlertCircle className="h-5 w-5 mr-2" />
            )}
            {message.text}
          </div>
        )}

        <div className="space-y-4 mb-6">
          {defaults.map(dayDefault => {
            const dayInfo = DAYS_OF_WEEK.find(d => d.value === dayDefault.day_of_week);
            return renderDayDefault(dayDefault, dayInfo);
          })}
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleSaveAll}
            disabled={saving}
            className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save All Defaults'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FirmWorkdayDefaultsManager;